import { Component, OnInit, Input, ChangeDetectorRef, ViewRef, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { PasswordRule } from 'app/core/auth/password-governance/models/password-rule';
import { AuthService } from 'app/core/auth';
import { Constants } from 'app/app.constants';
import { IAlert } from 'app/views/pages/ngbootstrap/alert/alert.component';
import { ChangePasswordService } from 'app/core/auth/password-governance/_services/change-password.service';
import { LayoutUtilsService } from 'app/core/_base/crud';
import { finalize, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
  standalone: false,
    selector: 'kt-password-governance',
    templateUrl: './password-governance.component.html',
    styleUrls: ['./password-governance.component.scss']
})
export class PasswordGovernanceComponent implements OnInit, OnDestroy {

    /**
     * Data alert
     */
    public alert: IAlert;

    /**
     * Eye to show/hide password
     */
    hide = true;

    /**
     * Variable indicating if the alert is displayed
     */
    public showedAlert = false;

    /**
     * Param for querie
     */
    @Input() public isReset: boolean;

    /**
     * Status for expired password
     */
    @Input() public isExpired: boolean;

    /**
     * Param for send to reset password
     */
    @Input() private userEncoded: string;

    /**
     * Formgroup change passwor
     */
    public changePasswordForm: UntypedFormGroup;

    /**
     * Password rules list
     */
    public listRules: Array<PasswordRule>;

    /**
     * Method that valide that the password comply with everything
     */
    private isCorrectPasswordRules: boolean;

    /**
     * Handle the form value changes subscription and must be closed to avoid memory leaks
     */
    public subscriptionForm: Subscription;

    /**
     * Status for loader in submit button
     */
    public loader: boolean;

    constructor(
        private router: Router,
        private fb: UntypedFormBuilder,
        private auth: AuthService,
        private changeDetectorRef: ChangeDetectorRef,
        private layoutUtilsService: LayoutUtilsService,
        private changePasswodService: ChangePasswordService,
        private translateService: TranslateService
    ) {
        this.loader = false;
    }

    ngOnInit() {
        this.initRegistrationForm();
        this.initPasswordRules();
        this.subscriptionForm = this.changePasswordForm.get('newPassword').valueChanges.subscribe(
            () => {
                this.validatePasswordRules();
            }
        );
    }

    ngOnDestroy(): void {
        this.subscriptionForm.unsubscribe();
    }

    /**
     * Form initalization
     * Default params, validators
     */
    private initRegistrationForm() {
        this.changePasswordForm = this.fb.group({
            currentPassword: ['', Validators.compose([
                Validators.required,
                Validators.minLength(Constants.atLeastCharacters),
            ])],
            newPassword: ['', Validators.compose([
                Validators.required,
                Validators.minLength(Constants.atLeastCharacters),
            ])],
            retypePassword: ['', Validators.compose([
                Validators.required,
                Validators.minLength(Constants.atLeastCharacters),
            ])]
        });
    }

    /**
     * Method to init the password rules
     * @author Castor Transformación Digital - Fabio Oquendo
     * @since 2020-04-06
     */
    private initPasswordRules() {
        let rule: PasswordRule;
        this.listRules = new Array<PasswordRule>();
        if (Constants.atLeastCharacters) {
            rule = new PasswordRule();
            rule.textRule = `Contains at least ${Constants.atLeastCharacters} characters`;
            rule.regex = new RegExp(`[A-Za-z0-9\d${Constants.specialsCharacters}].{${Constants.atLeastCharacters - 1},}$`);
            rule.value = false;
            this.listRules.push(rule);
        }

        if (Constants.containsLetter) {
            rule = new PasswordRule();
            rule.textRule = 'Contains a letter';
            rule.regex = new RegExp('[a-zA-Z]');
            rule.value = false;
            this.listRules.push(rule);
        }

		if (Constants.containsUpperCase) {
            rule = new PasswordRule();
            rule.textRule = 'Contains a UPPERCASE'
            rule.regex = new RegExp('[A-Z]');
            rule.value = false;
            this.listRules.push(rule);
        }
        if (Constants.containsLowerCase) {
            rule = new PasswordRule();
            rule.textRule = 'Contains a lowercase';
            rule.regex = new RegExp('[a-z]');
            rule.value = false;
            this.listRules.push(rule);
        }



        if (Constants.containsNumber) {
            rule = new PasswordRule();
            rule.textRule = 'Contains a number';
            rule.regex = new RegExp('[0-9]');
            rule.value = false;
            this.listRules.push(rule);
        }

        if (Constants.containsSpecial) {
            rule = new PasswordRule();
            rule.textRule = `Contains one special character ${Constants.specialsCharacters}`;
            rule.regex = new RegExp(`[${Constants.specialsCharacters}]`);
            rule.value = false;
            this.listRules.push(rule);
        }
    }

    /**
     * Method that valide the password typed
     * @author Castor Transformación Digital - Fabio Oquendo
     * @since 2020-04-06
     */
    public validatePasswordRules() {
        this.isCorrectPasswordRules = true;
        for (let i = 0; i < this.listRules.length; i++) {
            if (this.listRules[i].regex.test(this.changePasswordForm.controls.newPassword.value)) {
                this.listRules[i].value = true;
            } else {
                this.listRules[i].value = false;
                this.isCorrectPasswordRules = false;
            }
        }
    }

    /**
     * Checks if theres a new change in the dom to be forced
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2020-03-20
     */
    public updateDOM() {
        if (this.changeDetectorRef && !(this.changeDetectorRef as ViewRef).destroyed) {
            this.changeDetectorRef.detectChanges();
        }
    }

    /**
     * Method that valide and change password
     * @author Castor Transformación Digital - Fabio Oquendo
     * @since 2020-04-06
     */
    public submit() {

        if (this.isReset) {
            this.changePasswordForm.controls.currentPassword.setValue('U@123456');
        }

        const controls = this.changePasswordForm.controls;
        /** check form */
        if (this.changePasswordForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );
            return;
        }

        const currentPassword = controls.currentPassword.value;
        const newPassword = controls.newPassword.value;
        const retypePassword = controls.retypePassword.value;



        if (!this.isCorrectPasswordRules) {
            this.showAlert('danger', 'The password must comply with all the rules.');
            return;
        }

        if (!this.validateNewPassword(newPassword, retypePassword)) {
            this.showAlert('danger', 'The new password and retype password must be the same.');
            return;
        }
        this.loader = true;
        if (!this.isReset) {
            let usernameToSend: string;
            if (this.isExpired) {
                usernameToSend = this.userEncoded;
            } else {
                usernameToSend = localStorage.getItem('user');
            }

            let succesfullLogin = false;
            // validate password with login service
            this.auth.login(usernameToSend, currentPassword)
                .pipe(
                    tap(user => {
                        if (user) {
                            succesfullLogin = true;
                        }
                    }),
                    finalize(() => {
                        if (succesfullLogin) {
                            // Validate rules for password
                            if (!this.validateDiferentCurrentPassword(newPassword, currentPassword)) {
                                this.showAlert('danger', 'The new password must be different from the current password.');
								this.loader = false;
								this.updateDOM();
							} else {
                                this.closeAlert();
                                const obj = { username: usernameToSend, password: newPassword };
                                let logout = false;
                                // Then we can change the password and then make a logout
                                this.auth.logout(usernameToSend,true)
                                    .pipe(
                                        tap(logoutStatus => {
                                            logout = logoutStatus;
                                        }),
                                        switchMap(
                                            () => this.changePasswodService.changePassword(obj)
                                        ),
                                        finalize(
                                            () => {
                                                this.loader = false;
                                                this.updateDOM();
                                            }
                                        )
                                    )
                                    .subscribe(
                                        (data) => {
                                            if (data && logout) {
                                                this.router.navigate(['/auth/login']);
                                                this.showModalAlert(data);
                                            }
                                        }
                                    );
                            }
                        } else {
                            this.showAlert('danger', 'The current password is incorrect.');
                            this.loader = false;
                        }
                    })
                )
                .subscribe(() => { }, () => {
                    this.loader = false;
                    this.updateDOM();
                });
        } else {
            const userEncoded = this.router.url.split('user=')[1];
            this.changePasswodService.validateLink(userEncoded)
                .subscribe(auth => {
                    if (!auth) {
                        const modalRef = this.layoutUtilsService.generalAlert();
                        modalRef.componentInstance.data = this.translateService
                            .instant('AUTH.CHANGE_PASSWORD.EXPIRED_LINK');
                        this.router.navigateByUrl('/auth/login');
                    } else {
                        this.closeAlert();
                        const obj = { username: this.userEncoded, password: newPassword };
                        this.changePasswodService.changePassword(obj)
                            .pipe(
                                finalize(() => {
                                    this.changePasswodService.registerUsedLink(this.userEncoded)
                                        .pipe(
                                            finalize(() => this.loader = false)
                                        )
                                        .subscribe(
                                            data => {
                                                if (data) {
                                                    this.router.navigate(['/auth/login']);
                                                }
                                            }
                                        );
                                })
                            )
                            .subscribe(
                                (data) => {
                                    this.showModalAlert(data);
                                }
                            );
                    }
                });
        }

    }

    /**
     * Showing general message in alert/dialog window
     * @param message string to show
     */
    private showModalAlert(message: string) {
        const modalRef = this.layoutUtilsService.generalAlert();
        modalRef.componentInstance.data = message;
    }

    /**
     * Methode that show the alerts
     * @author Castor Transformación Digital - Fabio Oquendo
     * @since 2020-06-18
     */
    private showAlert(typeToSend: string, messageToSend: string) {
        this.alert = {
            id: 1,
            type: typeToSend,
            message: messageToSend,
        };
        this.showedAlert = true;
    }

    /**
     * Method that valide new password equal to retype password
     * @author Castor Transformación Digital - Fabio Oquendo
     * @since 2020-04-06
     */
    private validateNewPassword(newPassword: string, retypePassword: string): boolean {
        return newPassword === retypePassword;
    }

    /**
     * Method that valide new password equal to retype password
     * @author Castor Transformación Digital - Fabio Oquendo
     * @since 2020-04-06
     */
    private validateDiferentCurrentPassword(newPassword: string, currentPassword: string): boolean {
        return newPassword !== currentPassword;
    }

    /**
     * Methode that close the alerts
     * @author Castor Transformación Digital - Fabio Oquendo
     * @since 2020-06-18
     */
    public closeAlert() {
        this.showedAlert = false;
    }

    /**
     * Checking control validation
     * @param controlName: string => Equals to formControlName
     * @param validationType: string => Equals to valitors name
     */
    isControlHasError(controlName: string, validationType: string): boolean {
        const control = this.changePasswordForm.controls[controlName];
        if (!control) {
            return false;
        }

        const result =
            control.hasError(validationType) &&
            (control.dirty || control.touched);
        return result;
    }
}
